import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { forgotPassword } from '../services/auth';
import MailIcon from '../assets/images/Mail.svg';

export default function ForgotPasswordCard() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await forgotPassword({ email });
      
      if (response.success) {
        setEmailSent(true);
        toast.success(response.message || 'Password reset link has been sent to your email');
      } else {
        toast.error(response.error || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to send reset link. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div
        className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="flex flex-col items-center gap-6 w-full">
          {/* Mail Icon */}
          <div className="flex items-center justify-center">
            <img
              src={MailIcon}
              alt="Mail"
              className="w-32 h-32 md:w-40 md:h-40"
            />
          </div>
          
          {/* Heading */}
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-4xl leading-[1em]">
              Check your mailbox!
            </p>
            <p className="text-[#889696] font-poppins text-sm md:text-base font-normal max-w-xl mt-4">
              If this email is registered, you'll receive a password reset link shortly.
              <br />
              Please check your inbox
            </p>
          </div>
          
          {/* Back to Login Button */}
          <button
            type="button"
            onClick={() => navigate('/signin')}
            className="text-[#119975] font-poppins text-sm md:text-base font-medium hover:text-[#0d7a5f] transition-colors mt-2"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <div className="flex flex-col items-center gap-3 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-3xl md:text-[40px] leading-[1em] text-center">
          Reset your password
        </p>
        <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center mt-[1px] md:mt-4">
          Enter your registered email address, and we'll send you a verification code to reset your password
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-10">
        <div className="flex flex-col items-center gap-[30px] w-full">
          <div className="flex flex-col justify-center items-start gap-2 w-full md:w-[540.51px] h-[85px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              E-mail
            </label>
            <input
              type="email"
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full h-[60px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 outline-none font-poppins focus:border-[#119975] focus:bg-white transition-colors"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-lg font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {isSubmitting ? 'Sending...' : 'Send Reset Link'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => navigate('/signin')}
          className="text-[#119975] font-poppins font-medium text-sm"
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
}

